import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useAuthStore } from '../../store';
import api from '../../api/axios';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// ✅ public 폴더의 worker 파일 사용
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface Props {
  width: number;
  explanationPage?: number | null;
  showExplanation?: boolean;
  year?: number | null;
  round?: number | null;
  subjectId?: number | null;
  subjectName?: string | null; // ✅ 필기 과목명
  examType?: string;
}

const MEMO_KEY = 'mohabwe_memo';
const BG_COLORS = [
  { label: '흰색', value: '#ffffff' },
  { label: '아이보리', value: '#fffef0' },
  { label: '연노랑', value: '#fefce8' },
  { label: '연녹색', value: '#f0fdf4' },
  { label: '연파랑', value: '#f0f9ff' },
  { label: '검정', value: '#1a1a1a' },
  { label: '진회색', value: '#374151' },
];

const PDF_KEY = 'mohabwe_pdf_url'; // ✅ PDF URL 저장 키

const RightPanel: React.FC<Props> = ({ width, explanationPage, showExplanation, year, round, subjectId, subjectName, examType }) => {
  const { isAdmin, isLoggedIn } = useAuthStore();
  const [bgColor, setBgColor] = useState('#ffffff');
  const [memo, setMemo] = useState('');
  const [memoSaved, setMemoSaved] = useState(false);
  const [pdfType, setPdfType] = useState<'pdf' | 'image' | null>(null); // ✅ 파일 타입 별도 관리
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState('');
  const [numPages, setNumPages] = useState<number>(0);
  const [mode, setMode] = useState<'memo' | 'pdf'>('memo');
  const [serverPdfUrl, setServerPdfUrl] = useState(''); // ✅ 서버 URL 입력
  // ✅ 강의 북마크
  // ✅ 과목별 북마크 - localStorage 저장
  const getLectureKey = () => `lecture_bm_${examType || ''}_${subjectId || 0}_${subjectName || ''}`;
  const loadLectureItems = () => {
    try {
      const key = getLectureKey();
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  };
  const [lectureItems, setLectureItemsRaw] = useState<{title: string; page: number; icon: string}[]>([]);
  const setLectureItems = (items: {title: string; page: number; icon: string}[]) => {
    setLectureItemsRaw(items);
    try { localStorage.setItem(getLectureKey(), JSON.stringify(items)); } catch {}
    if (lectureChannelRef.current && lectureChannelAliveRef.current) {
      try { lectureChannelRef.current.postMessage({ type: 'items', items }); } catch {}
    }
  };
  // ✅ 강의용 PDF 슬롯 (슬롯B)
  const [lecturePdfUrl, setLecturePdfUrl] = useState<string | null>(null);
  const [lecturePdfName, setLecturePdfName] = useState('');
  const [isLectureMode, setIsLectureMode] = useState(false); // true=강의PDF, false=해설PDF
  const lectureFileInputRef = useRef<HTMLInputElement>(null);
  const [showLectureInput, setShowLectureInput] = useState(false);
  const [lectureTitle, setLectureTitle] = useState('');
  const [lecturePage, setLecturePage] = useState('');
  const [lectureIcon, setLectureIcon] = useState('⚡');
  const [manualPage, setManualPage] = useState<number | null>(null);
  const lectureChannelRef = useRef<BroadcastChannel | null>(null);
  const lectureChannelAliveRef = useRef(false); // ✅ 채널 열림 여부 (닫힌 채널 postMessage 방지)
  const lectureWindowRef = useRef<Window | null>(null);
  // ✅ 관리자 PDF 드래그 스크롤
  const pdfScrollRef = useRef<HTMLDivElement>(null);
  const dragScrolling = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  const onPdfMouseDown = (e: React.MouseEvent) => {
    if (!isAdmin) return;
    dragScrolling.current = true;
    dragStart.current = {
      x: e.clientX, y: e.clientY,
      scrollLeft: pdfScrollRef.current?.scrollLeft || 0,
      scrollTop: pdfScrollRef.current?.scrollTop || 0,
    };
    e.preventDefault();
  };
  const onPdfMouseMove = (e: React.MouseEvent) => {
    if (!dragScrolling.current || !pdfScrollRef.current) return;
    pdfScrollRef.current.scrollLeft = dragStart.current.scrollLeft - (e.clientX - dragStart.current.x);
    pdfScrollRef.current.scrollTop = dragStart.current.scrollTop - (e.clientY - dragStart.current.y);
  };
  // ✅ 마우스 휠로 페이지 이동
  const onPdfWheel = (e: React.WheelEvent) => {
    if (!isAdmin) return;
    e.preventDefault();
    if (e.deltaY > 0) {
      // 아래 스크롤 → 다음 페이지
      if (currentPage < numPages) setManualPage(currentPage + 1);
    } else {
      // 위 스크롤 → 이전 페이지
      if (currentPage > 1) setManualPage(currentPage - 1);
    }
  };

  const onPdfMouseUp = (e: React.MouseEvent) => {
    if (!dragScrolling.current) return;
    dragScrolling.current = false;
    // ✅ 드래그 거리로 페이지 이동 판단
    const deltaX = e.clientX - dragStart.current.x;
    const deltaY = e.clientY - dragStart.current.y;
    const THRESHOLD = 60; // 60px 이상 드래그 시 페이지 이동
    // 좌우 드래그가 상하보다 클 때만 페이지 이동
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > THRESHOLD) {
      if (deltaX < 0) {
        // 왼쪽 드래그 → 다음 페이지
        if (currentPage < numPages) setManualPage(currentPage + 1);
      } else {
        // 오른쪽 드래그 → 이전 페이지
        if (currentPage > 1) setManualPage(currentPage - 1);
      }
    }
    // 상하 드래그가 클 때는 스크롤 유지 (페이지 이동 없음)
  };
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [pdfNotFound, setPdfNotFound] = useState<string | null>(null);   // 파일 없음 (404)
  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null); // 로드 실패 (네트워크/서버오류)
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [pdfContainerHeight, setPdfContainerHeight] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pdfNotFoundTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const pdfLoadErrorTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const showNotFound = (fileName: string) => {
    if (pdfNotFoundTimer.current) clearTimeout(pdfNotFoundTimer.current);
    setPdfNotFound(fileName);
    pdfNotFoundTimer.current = setTimeout(() => setPdfNotFound(null), 6000);
  };
  const showLoadError = (msg: string) => {
    if (pdfLoadErrorTimer.current) clearTimeout(pdfLoadErrorTimer.current);
    setPdfLoadError(msg);
    pdfLoadErrorTimer.current = setTimeout(() => setPdfLoadError(null), 6000);
  };

  // ✅ year/round 변경 시 파일명 규칙으로 해설 PDF 자동 로드
  // 규칙: /media/pdf/{종목명}_{필기|실기}_{년도}_{차수}차해설집.pdf
  useEffect(() => {
    // ✅ examType/subjectId 변경 시 이전 PDF 초기화
    if (pdfUrl && pdfUrl.startsWith('blob:')) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfType(null);
    setPdfName('');
    localStorage.removeItem(PDF_KEY);

    if (!year || !round || !subjectId) return;

    import('../../api').then(({ questionAPI }) => {
      questionAPI.subjects(examType).then(async (res: any) => {
        const subject = res.data.find((s: any) => s.id === subjectId);
        if (!subject) return;
        const fileName = examType === '필기' && subjectName
          ? `${subject.name}_${examType}_${subjectName}_${year}_${round}차해설집.pdf`
          : `${subject.name}_${examType}_${year}_${round}차해설집.pdf`;
        const url = `/media/pdf/${encodeURIComponent(fileName)}`;

        // ✅ fetch로 실제 파일 요청 → 상태코드로 정확히 구분
        try {
          const check = await fetch(url);
          if (check.status === 404) {
            showNotFound(fileName);
            return;
          }
          if (!check.ok) {
            showLoadError(`서버 오류 (${check.status}): ${fileName}`);
            return;
          }
          // ✅ Content-Type 확인 (pdf / octet-stream 모두 허용)
          const contentType = check.headers.get('content-type') || '';
          if (contentType.includes('text/html') || contentType.includes('text/plain')) {
            // HTML 에러페이지가 내려온 경우 → 실제로는 없는 파일
            showNotFound(fileName);
            return;
          }
          // ✅ blob URL로 변환해서 로드
          const blob = await check.blob();
          const blobUrl = URL.createObjectURL(blob);
          if (pdfUrl && pdfUrl.startsWith('blob:')) URL.revokeObjectURL(pdfUrl);
          setPdfUrl(blobUrl);
          setPdfType('pdf');
          setPdfName(fileName);
          localStorage.setItem(PDF_KEY, JSON.stringify({ url, name: fileName }));
          setMode('pdf');
        } catch {
          showLoadError(`네트워크 오류: ${fileName}`);
        }
      }).catch(() => {});
    });
  }, [year, round, subjectId, subjectName, examType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ 저장된 PDF URL 복원 (blob URL은 세션 만료로 무효 → 무시)
  useEffect(() => {
    const saved = localStorage.getItem(PDF_KEY);
    if (saved) {
      try {
        const { url, name } = JSON.parse(saved);
        if (url && !url.startsWith('blob:')) {
          setPdfUrl(url);
          setPdfName(name || url);
          setPdfType('pdf');
        } else {
          // blob URL은 무효화됐으므로 삭제
          localStorage.removeItem(PDF_KEY);
        }
      } catch (e) {}
    }
  }, []);

  // ✅ 컨테이너 높이 감지 - PDF 크기 자동 맞춤
  useEffect(() => {
    if (!pdfContainerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setPdfContainerHeight(entry.contentRect.height);
      }
    });
    observer.observe(pdfContainerRef.current);
    return () => observer.disconnect();
  }, []);

  // ✅ 메모 로드 - DB 우선, 없으면 localStorage
  useEffect(() => {
    const loadMemo = async () => {
      if (isLoggedIn) {
        try {
          const res = await api.get('/api/questions/memo/');
          if (res.data.memo) {
            setMemo(res.data.memo);
            localStorage.setItem(MEMO_KEY, res.data.memo);
            return;
          }
        } catch (e) {}
      }
      // localStorage fallback
      const saved = localStorage.getItem(MEMO_KEY);
      if (saved) setMemo(saved);
    };
    loadMemo();
  }, [isLoggedIn]);

  // ✅ 메모 저장 - localStorage + DB
  const saveMemo = useCallback(async () => {
    localStorage.setItem(MEMO_KEY, memo);
    if (isLoggedIn) {
      try {
        await api.post('/api/questions/memo/', { memo });
      } catch (e) {}
    }
    setMemoSaved(true);
    setTimeout(() => setMemoSaved(false), 2000);
  }, [memo, isLoggedIn]);

  // Ctrl+S 저장
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        if ((e.target as HTMLElement).tagName === 'TEXTAREA') {
          e.preventDefault();
          saveMemo();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [saveMemo]);

  // 로컬 파일 로드
  // ✅ 팝업창에 PDF URL 전송
  const sendPdfToPopup = (url: string, name: string, type: string) => {
    if (lectureChannelRef.current && lectureChannelAliveRef.current) {
      try {
        lectureChannelRef.current.postMessage({
          type: 'pdf_loaded', url, name, pdfType: type,
        });
      } catch {}
    }
  };

  const handleFileLoad = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (pdfUrl && pdfUrl.startsWith('blob:')) URL.revokeObjectURL(pdfUrl);
    if (file.type === 'application/pdf') {
      const url = URL.createObjectURL(file);
      setPdfUrl(url);
      setPdfType('pdf');
      setPdfName(file.name);
      localStorage.removeItem(PDF_KEY);
      sendPdfToPopup(url, file.name, 'pdf');
    } else if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPdfUrl(url);
      setPdfType('image');
      setPdfName(file.name);
      localStorage.removeItem(PDF_KEY);
      sendPdfToPopup(url, file.name, 'image');
    } else {
      alert('이미지 또는 PDF 파일만 지원합니다.');
    }
    e.target.value = '';
  };

  // ✅ 서버 URL로 PDF 로드
  const handleServerUrl = () => {
    if (!serverPdfUrl.trim()) return;
    const url = serverPdfUrl.trim();
    if (pdfUrl && pdfUrl.startsWith('blob:')) URL.revokeObjectURL(pdfUrl);
    const name = url.split('/').pop() || url;
    setPdfUrl(url);
    setPdfType('pdf'); // ✅ 서버 URL은 항상 PDF로 처리
    setPdfName(name);
    localStorage.setItem(PDF_KEY, JSON.stringify({ url, name }));
    setShowUrlInput(false);
    setServerPdfUrl('');
    sendPdfToPopup(url, name, 'pdf');
  };

  const handleFileClose = () => {
    if (pdfUrl && pdfUrl.startsWith('blob:')) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfType(null);
    setPdfName('');
    setNumPages(0);
    localStorage.removeItem(PDF_KEY);
  };

  const isDark = bgColor === '#1a1a1a' || bgColor === '#374151';
  const mutedColor = isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted)';

  // ✅ 현재 표시할 페이지 - 유효성 검사 추가
  const currentPage = (() => {
    const page = manualPage ?? explanationPage ?? 1;
    if (page < 1) return 1;
    if (numPages > 0 && page > numPages) return numPages;
    return page;
  })();

  // ✅ PDF 로드 완료(numPages>0) 시 showExplanation이면 해당 페이지로 이동
  const [pageKey, setPageKey] = useState(0);
  const prevNumPages = useRef(0);
  useEffect(() => {
    // numPages가 0→N으로 바뀔 때 (PDF 로드 완료)
    if (numPages > 0 && prevNumPages.current === 0 && showExplanation) {
      setPageKey(k => k + 1);
    }
    prevNumPages.current = numPages;
  }, [numPages]);

  // ✅ manualPage 바뀌면 즉시 pageKey 증가 (페이지 이동 보장)
  useEffect(() => {
    if (manualPage !== null) {
      setPageKey(k => k + 1);
    }
  }, [manualPage]);

  // ✅ 과목 변경 시 해당 과목 북마크 로드
  useEffect(() => {
    const items = loadLectureItems();
    setLectureItemsRaw(items);
    if (lectureChannelRef.current && lectureChannelAliveRef.current) {
      const subjectLabel = subjectName || (examType === '실기' ? '실기' : '');
      try {
        lectureChannelRef.current.postMessage({
          type: 'items', items,
          subject: subjectLabel,
          examType: examType || '',
        });
      } catch {}
    }
  }, [subjectId, subjectName, examType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // showExplanation이 true가 될 때 + PDF 이미 로드된 경우
    if (showExplanation && numPages > 0) {
      setPageKey(k => k + 1);
    }
  }, [showExplanation, explanationPage]);

  const isPdf = pdfType === 'pdf';
  const isImage = pdfType === 'image';
  // ✅ 관리자 or 수동 PDF 열기 or 해설보기 클릭 or PDF 로드 완료 시 PDF 영역 표시
  // ✅ BroadcastChannel - 팝업창에서 페이지 이동 수신
  useEffect(() => {
    const ch = new BroadcastChannel('lecture_bookmark');
    lectureChannelRef.current = ch;
    lectureChannelAliveRef.current = true; // ✅ 채널 열림
    ch.onmessage = (e) => {
      if (e.data?.type === 'goto_page') {
        setManualPage(e.data.page);
        setPageKey(k => k + 1);
      }
      if (e.data?.type === 'sync_items') {
        const subjectLabel = subjectName || (examType === '실기' ? '실기' : '');
        if (lectureChannelAliveRef.current) {
          try {
            ch.postMessage({
              type: 'items', items: lectureItems,
              subject: subjectLabel,
              examType: examType || '',
            });
          } catch {}
        }
      }
      if (e.data?.type === 'items_from_popup') {
        setLectureItems(e.data.items || []);
      }
      if (e.data?.type === 'pdf_request') {
        // 팝업창에서 현재 PDF URL 요청
        if (pdfUrl && lectureChannelAliveRef.current) {
          try {
            ch.postMessage({ type: 'pdf_loaded', url: pdfUrl, name: pdfName, pdfType: isPdf ? 'pdf' : 'image' });
          } catch {}
        }
      }
      if (e.data?.type === 'open_pdf_from_popup') {
        // 팝업창에서 파일 열기 → base64 → RightPanel에서 PDF 열기
        const { base64, name } = e.data;
        if (base64) {
          fetch(base64)
            .then(res => res.blob())
            .then(blob => {
              const url = URL.createObjectURL(blob);
              if (pdfUrl && pdfUrl.startsWith('blob:')) URL.revokeObjectURL(pdfUrl);
              setPdfUrl(url);
              setPdfType('pdf');
              setPdfName(name || 'PDF');
              setManualPage(1);
              localStorage.removeItem('mohabwe_pdf_url');
            });
        }
      }
    };
    return () => {
      lectureChannelAliveRef.current = false; // ✅ close 전에 먼저 내림
      try { ch.close(); } catch {}
    };
  }, [lectureItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ 팝업창 열기
  const openLecturePopup = () => {
    const ch = lectureChannelRef.current;
    const popup = window.open('/lecture-bookmark.html', 'lecture_bookmark',
      'width=600,height=500,resizable=yes,scrollbars=yes');
    lectureWindowRef.current = popup;
    // 팝업 로드 후 아이템 전송
    setTimeout(() => {
      if (ch && lectureChannelAliveRef.current) {
        const subjectLabel = subjectName || (examType === '실기' ? '실기' : '');
        try {
          ch.postMessage({
            type: 'items', items: lectureItems,
            subject: subjectLabel,
            examType: examType || '',
          });
        } catch {}
      }
    }, 800);
  };

  // ✅ 현재 표시할 PDF (강의모드: 강의PDF, 일반모드: 해설PDF)
  const activePdfUrl = isLectureMode && lecturePdfUrl ? lecturePdfUrl : pdfUrl;
  const activePdfName = isLectureMode && lecturePdfUrl ? lecturePdfName : pdfName;
  const showPdfArea = isAdmin || mode === 'pdf' || !!showExplanation || !!pdfUrl || !!lecturePdfUrl;

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <div style={{
      width: isMobile ? '100%' : width,
      minWidth: isMobile ? 0 : width,
      maxWidth: isMobile ? '100vw' : undefined,
      background: bgColor,
      borderLeft: isMobile ? 'none' : '1px solid var(--border)',
      borderTop: isMobile ? '2px solid var(--border)' : 'none',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden', flexShrink: 0,
      transition: 'background 0.2s',
      position: 'relative',
      boxSizing: 'border-box',
    }}>

      {/* ✅ 해설 PDF 파일 없음 토스트 (404) */}
      {pdfNotFound && (
        <div style={{
          position: 'absolute', bottom: 24, right: 16, zIndex: 9999,
          background: '#92400e', color: 'white',
          padding: '12px 16px', borderRadius: 10,
          fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column', gap: 6, minWidth: 260,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>📂</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>해설 PDF 파일 없음</div>
              <div style={{ fontSize: 11, color: '#fde68a', marginTop: 2 }}>{pdfNotFound}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>서버에 파일을 업로드해 주세요.</div>
            </div>
          </div>
          <button onClick={() => { if (pdfNotFoundTimer.current) clearTimeout(pdfNotFoundTimer.current); setPdfNotFound(null); }}
            style={{ alignSelf: 'flex-end', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 12, padding: '4px 12px', borderRadius: 6, fontWeight: 700 }}>
            확인
          </button>
        </div>
      )}

      {/* ✅ 해설 PDF 로드 실패 토스트 (네트워크/서버 오류) */}
      {pdfLoadError && (
        <div style={{
          position: 'absolute', bottom: 24, right: 16, zIndex: 9999,
          background: '#7f1d1d', color: 'white',
          padding: '12px 16px', borderRadius: 10,
          fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column', gap: 6, minWidth: 260,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>❌</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>PDF 로드 실패</div>
              <div style={{ fontSize: 11, color: '#fca5a5', marginTop: 2 }}>{pdfLoadError}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>네트워크 또는 서버 오류입니다.</div>
            </div>
          </div>
          <button onClick={() => { if (pdfLoadErrorTimer.current) clearTimeout(pdfLoadErrorTimer.current); setPdfLoadError(null); }}
            style={{ alignSelf: 'flex-end', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 12, padding: '4px 12px', borderRadius: 6, fontWeight: 700 }}>
            확인
          </button>
        </div>
      )}

      {/* ===== 툴바 ===== */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '5px 10px', borderBottom: '1px solid var(--border)',
        background: 'rgba(255,255,255,0.9)', flexShrink: 0,
        flexWrap: 'wrap', minHeight: 36,
      }}>
        {/* 일반유저: 메모/해설 탭 */}
        {!isAdmin && (
          <div style={{ display: 'flex', gap: 4, marginRight: 6 }}>
            {(['memo', 'pdf'] as const).map(m => (
              <button key={m} onClick={() => setMode(m)} style={{
                fontSize: 11, padding: '2px 10px', height: 24,
                border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 700,
                background: mode === m ? 'var(--primary)' : '#f1f5f9',
                color: mode === m ? 'white' : 'var(--text-secondary)',
              }}>
                {m === 'memo' ? '📝 메모' : '📄 해설'}
              </button>
            ))}
          </div>
        )}

        {/* 배경색 */}
        <span style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>배경:</span>
        {BG_COLORS.map(c => (
          <button key={c.value} title={c.label} onClick={() => setBgColor(c.value)} style={{
            width: 16, height: 16, borderRadius: '50%', background: c.value,
            border: bgColor === c.value ? '2px solid var(--primary)' : '1px solid #ccc',
            cursor: 'pointer', padding: 0, flexShrink: 0,
          }} />
        ))}

        {/* 파일 열기 */}
        {showPdfArea && (
          <>
            <div style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 2px' }} />
            {/* 로컬 파일 열기 */}
            <button className="btn btn-ghost btn-sm"
              style={{ fontSize: 11, padding: '2px 8px', height: 24 }}
              onClick={() => fileInputRef.current?.click()}>
              📂 {pdfUrl && !showUrlInput ? (pdfName || '다른 파일') : '파일 열기'}
            </button>
            {/* ✅ 서버 URL 버튼 */}
            <button className="btn btn-ghost btn-sm"
              style={{ fontSize: 11, padding: '2px 8px', height: 24 }}
              onClick={() => setShowUrlInput(!showUrlInput)}
              title="서버 PDF URL로 열기">
              🌐 URL
            </button>
            {/* ✅ 강의용 파일열기 버튼 - 관리자만 */}
            {isAdmin && (
              <button className="btn btn-ghost btn-sm"
                style={{ fontSize: 11, padding: '2px 8px', height: 24, color: '#059669', borderColor: '#059669' }}
                onClick={() => lectureFileInputRef.current?.click()}
                title="강의용 PDF 열기">
                🎓 강의열기
              </button>
            )}
            {/* ✅ 강의 전환 버튼 - 강의PDF 있을 때만 */}
            {isAdmin && lecturePdfUrl && (
              <button className="btn btn-ghost btn-sm"
                style={{
                  fontSize: 11, padding: '2px 8px', height: 24,
                  color: isLectureMode ? '#d97706' : '#7c3aed',
                  borderColor: isLectureMode ? '#d97706' : '#7c3aed',
                  fontWeight: 700,
                }}
                onClick={() => setIsLectureMode(!isLectureMode)}
                title={isLectureMode ? '해설PDF로 전환' : '강의PDF로 전환'}>
                {isLectureMode ? '📖 해설' : '🎓 강의'}
              </button>
            )}
            {/* ✅ 북마크 버튼 */}
            {isAdmin && (
              <button className="btn btn-ghost btn-sm"
                style={{ fontSize: 11, padding: '2px 8px', height: 24, color: '#7c3aed', borderColor: '#7c3aed' }}
                onClick={openLecturePopup}
                title="강의 북마크 팝업창 열기">
                📚
              </button>
            )}
            {isAdmin && (
              <button className="btn btn-ghost btn-sm"
                style={{ fontSize: 11, padding: '2px 8px', height: 24 }}
                onClick={() => setShowLectureInput(true)}
                title="북마크 설정">
                ⚙️
              </button>
            )}
            {pdfUrl && (
              <button className="btn btn-ghost btn-sm"
                style={{ fontSize: 11, padding: '2px 8px', height: 24 }}
                onClick={handleFileClose}>✕ 닫기</button>
            )}
            <input ref={fileInputRef} type="file"
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              onChange={handleFileLoad} />
            {/* ✅ 강의용 파일 input */}
            <input ref={lectureFileInputRef} type="file"
              accept="application/pdf"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const url = URL.createObjectURL(file);
                setLecturePdfUrl(url);
                setLecturePdfName(file.name);
                setIsLectureMode(true);
                setManualPage(1);
                // 강의PDF 열리면 북마크 팝업 자동 오픈
                setTimeout(() => openLecturePopup(), 300);
                e.target.value = '';
              }} />
          </>
        )}

        {/* ✅ 서버 URL 입력창 */}
        {showPdfArea && showUrlInput && (
          <div style={{
            width: '100%', display: 'flex', gap: 4, padding: '4px 0',
            borderTop: '1px solid var(--border)', marginTop: 2,
          }}>
            <input
              style={{ flex: 1, fontSize: 11, padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 4 }}
              placeholder="예: /media/pdf/해설집.pdf 또는 https://..."
              value={serverPdfUrl}
              onChange={e => setServerPdfUrl(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleServerUrl()}
            />
            <button className="btn btn-primary btn-sm"
              style={{ fontSize: 11, padding: '2px 8px', height: 24 }}
              onClick={handleServerUrl}>열기</button>
            <button className="btn btn-ghost btn-sm"
              style={{ fontSize: 11, padding: '2px 8px', height: 24 }}
              onClick={() => setShowUrlInput(false)}>✕</button>
          </div>
        )}

        {/* ✅ 페이지 표시 + 관리자 이동 버튼 */}
        {showPdfArea && isPdf && numPages > 0 && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            {isAdmin && (
              <button className="btn btn-ghost btn-sm"
                style={{ fontSize: 12, padding: '2px 8px', height: 24 }}
                onClick={() => { if (currentPage > 1) setManualPage(currentPage - 1); }}>
                ◀
              </button>
            )}
            <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {currentPage} / {numPages}p
            </span>
            {isAdmin && (
              <>
                <button className="btn btn-ghost btn-sm"
                  style={{ fontSize: 12, padding: '2px 8px', height: 24 }}
                  onClick={() => { if (currentPage < numPages) setManualPage(currentPage + 1); }}>
                  ▶
                </button>
                <input
                  type="number" min={1} max={numPages}
                  value={currentPage}
                  onChange={e => {
                    const p = Number(e.target.value);
                    if (p >= 1 && p <= numPages) setManualPage(p);
                  }}
                  style={{
                    width: 46, fontSize: 11, padding: '2px 4px',
                    border: '1px solid var(--border)', borderRadius: 4,
                    textAlign: 'center', height: 24,
                  }}
                />
              </>
            )}
          </div>
        )}

        {/* 메모 저장 */}
        {!isAdmin && mode === 'memo' && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            {memoSaved && <span style={{ fontSize: 10, color: '#2ecc71', fontWeight: 700 }}>✅ 저장됨</span>}
            <button className="btn btn-primary btn-sm"
              style={{ fontSize: 11, padding: '2px 8px', height: 24 }}
              onClick={saveMemo}>💾 저장</button>
          </div>
        )}
      </div>

      {/* ===== 본문 ===== */}
      <div style={{ flex: 1, overflow: isMobile ? 'auto' : 'hidden', position: 'relative' }}>

        {/* 메모장 */}
        {!isAdmin && mode === 'memo' && (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{
              padding: '10px 14px 8px', fontSize: 12, color: mutedColor,
              borderBottom: '1px solid var(--border)', lineHeight: 1.6, flexShrink: 0,
            }}>
              📝 공부하면서 메모할 수 있는 메모창입니다.<br />
              <span style={{ fontSize: 11 }}>💾 저장 또는 Ctrl+S로 저장하세요.</span>
            </div>
            <textarea value={memo} onChange={e => setMemo(e.target.value)}
              placeholder="여기에 메모를 입력하세요..."
              style={{
                flex: 1, border: 'none', outline: 'none', resize: 'none',
                background: 'transparent', padding: '12px', fontSize: 13,
                lineHeight: 1.7, fontFamily: 'inherit',
                color: isDark ? 'white' : 'var(--text-primary)',
                boxSizing: 'border-box',
              }} />
          </div>
        )}

        {/* PDF/이미지 뷰어 */}
        {showPdfArea && (
          <div style={{ width: '100%', height: isMobile ? 'auto' : '100%', position: 'relative', overflow: isMobile ? 'auto' : 'hidden', overflowX: isMobile ? 'auto' : 'hidden' }}>
            {pdfUrl ? (
              <>
                {/* 이미지 */}
                {isImage && (
                  <img src={pdfUrl} alt="loaded"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                )}

                {/* ✅ PDF - 창 크기에 맞게 자동 조절 */}
                {isPdf && (
                  <div
                    ref={(el) => { (pdfContainerRef as any).current = el; (pdfScrollRef as any).current = el; }}
                    style={{
                      width: '100%', height: isMobile ? 'auto' : '100%',
                      overflow: isAdmin ? 'auto' : isMobile ? 'visible' : 'hidden',
                      display: 'flex', justifyContent: isMobile ? 'flex-start' : 'center', alignItems: 'flex-start',
                      paddingLeft: 0,
                      pointerEvents: isAdmin ? 'auto' : 'none',
                      cursor: isAdmin ? (dragScrolling.current ? 'grabbing' : 'grab') : 'default',
                      userSelect: 'none',
                    }}
                    onMouseDown={onPdfMouseDown}
                    onMouseMove={onPdfMouseMove}
                    onMouseUp={onPdfMouseUp}
                    onMouseLeave={onPdfMouseUp}
                    onWheel={onPdfWheel}
                  >
                    <Document
                      file={activePdfUrl}
                      onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                      loading={<div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>PDF 로딩중...</div>}
                      error={
                        <div style={{ padding: 20, textAlign: 'center' }}>
                          <div style={{ fontSize: 20, marginBottom: 8 }}>❌</div>
                          <div style={{ color: '#dc2626', fontWeight: 700, fontSize: 13 }}>PDF 로드 실패</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4 }}>파일이 손상되었거나 접근할 수 없습니다.</div>
                        </div>
                      }
                    >
                      <Page
                        key={`${pageKey}-${currentPage}`}
                        pageNumber={currentPage}
                        width={isMobile ? window.innerWidth + 10 : width - 20}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                  </div>
                )}

                {/* ✅ 일반유저 블러 스크린 - 해설보기 전 */}
                {!isAdmin && !showExplanation && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    backdropFilter: 'blur(14px)',
                    background: 'rgba(255,255,255,0.45)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    gap: 10, pointerEvents: 'all',
                  }}>
                    <div style={{ fontSize: 32 }}>🔒</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#374151' }}>해설 잠김</div>
                    <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', lineHeight: 1.6 }}>
                      아래 <strong>해설보기</strong> 버튼을 누르면<br />해설이 표시됩니다.
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* 파일 없을 때 */
              <div style={{
                width: '100%', height: '100%',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                color: mutedColor, gap: 8,
              }}>
                <div style={{ fontSize: 36 }}>📂</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? 'white' : 'var(--text-primary)' }}>
                  해설 PDF를 열어주세요
                </div>
                <div style={{ fontSize: 11, textAlign: 'center', lineHeight: 1.8, color: mutedColor }}>
                  이미지 / PDF 지원<br />
                  <span style={{ fontSize: 10 }}>PPT·Word → PDF 변환 후 사용</span>
                </div>
                <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }}
                  onClick={() => fileInputRef.current?.click()}>
                  파일 열기
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ✅ 강의 북마크 설정 모달 */}
      {showLectureInput && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setShowLectureInput(false)}>
          <div style={{
            background: 'white', borderRadius: 14, padding: 24,
            width: 480, minWidth: 320, maxWidth: '95vw',
            maxHeight: '85vh', boxShadow: '0 12px 50px rgba(0,0,0,0.25)',
            display: 'flex', flexDirection: 'column', gap: 14,
            resize: 'both', overflow: 'auto',
          }} onClick={e => e.stopPropagation()}>

            {/* 헤더 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 800, fontSize: 16, color: '#7c3aed' }}>📚 강의 북마크 설정</span>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8' }}
                onClick={() => setShowLectureInput(false)}>✕</button>
            </div>

            {/* 아이콘 선택 */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>아이콘 선택</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {[
                  '⚡','🔌','🔋','💡','🔧','⚙️','🔩','📐','📏','🧲',
                  '🌡️','📊','📈','📉','🔬','🔭','💻','🖥️','📡','🛠️',
                  '🏭','🏗️','🔆','💥','⚠️','📋','📌','📍','🗂️','📁',
                  '✅','❌','⭕','🔴','🟠','🟡','🟢','🔵','🟣','⚫',
                ].map(icon => (
                  <button key={icon}
                    style={{
                      width: 36, height: 36, fontSize: 18, border: lectureIcon === icon ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                      borderRadius: 6, cursor: 'pointer', background: lectureIcon === icon ? '#ede9fe' : 'white',
                    }}
                    onClick={() => setLectureIcon(icon)}>{icon}</button>
                ))}
              </div>
            </div>

            {/* 입력 */}
            <div style={{ display: 'flex', gap: 6 }}>
              <span style={{ fontSize: 24, lineHeight: '36px' }}>{lectureIcon}</span>
              <input
                style={{ flex: 1, fontSize: 14, padding: '7px 12px',
                  border: '1.5px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: 'inherit' }}
                placeholder="북마크 제목"
                value={lectureTitle}
                onChange={e => setLectureTitle(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && lectureTitle.trim() && lecturePage) {
                    const newItems = [...lectureItems, { title: lectureTitle.trim(), page: Number(lecturePage), icon: lectureIcon }];
                    setLectureItems(newItems);
                    if (lectureChannelRef.current && lectureChannelAliveRef.current) { try { lectureChannelRef.current.postMessage({ type: 'items', items: newItems }); } catch {} }
                    setLectureTitle(''); setLecturePage('');
                  }
                }}
              />
              <input
                style={{ width: 72, fontSize: 14, padding: '7px 8px',
                  border: '1.5px solid #e2e8f0', borderRadius: 8, outline: 'none',
                  textAlign: 'center', fontFamily: 'inherit' }}
                placeholder="페이지"
                type="number" min={1}
                value={lecturePage}
                onChange={e => setLecturePage(e.target.value)}
              />
              <button
                style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8,
                  cursor: 'pointer', fontSize: 14, fontWeight: 700, padding: '0 16px',
                  opacity: (!lectureTitle.trim() || !lecturePage) ? 0.4 : 1 }}
                disabled={!lectureTitle.trim() || !lecturePage}
                onClick={() => {
                  const newItems = [...lectureItems, { title: lectureTitle.trim(), page: Number(lecturePage), icon: lectureIcon }];
                  setLectureItems(newItems);
                  if (lectureChannelRef.current && lectureChannelAliveRef.current) { try { lectureChannelRef.current.postMessage({ type: 'items', items: newItems }); } catch {} }
                  setLectureTitle(''); setLecturePage('');
                }}>추가</button>
            </div>

            {/* 목록 */}
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 0 }}>
              {lectureItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                  북마크를 추가하세요
                </div>
              ) : (
                lectureItems.map((item, idx) => (
                  <div key={idx} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', borderRadius: 10,
                    border: '1.5px solid #ede9fe', background: '#faf5ff',
                    cursor: 'pointer',
                  }}
                    onClick={() => {
                      setManualPage(item.page);
                      setPageKey(k => k + 1);
                      setShowLectureInput(false);
                    }}>
                    <span style={{ fontSize: 22 }}>{item.icon}</span>
                    <span style={{ fontSize: 14, flex: 1, fontWeight: 600 }}>{item.title}</span>
                    <span style={{ fontSize: 12, color: '#7c3aed', fontWeight: 700, background: '#ede9fe',
                      padding: '2px 8px', borderRadius: 4 }}>{item.page}p</span>
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer',
                      color: '#94a3b8', fontSize: 16, padding: '0 4px', lineHeight: 1 }}
                      onClick={e => {
                        e.stopPropagation();
                        const newItems = lectureItems.filter((_, i) => i !== idx);
                        setLectureItems(newItems);
                        if (lectureChannelRef.current && lectureChannelAliveRef.current) { try { lectureChannelRef.current.postMessage({ type: 'items', items: newItems }); } catch {} }
                      }}>✕</button>
                  </div>
                ))
              )}
            </div>

            {/* 팝업 열기 버튼 */}
            <button
              style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8,
                cursor: 'pointer', fontSize: 14, fontWeight: 700, padding: '10px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              onClick={() => { openLecturePopup(); setShowLectureInput(false); }}>
              📚 강의 북마크 팝업창 열기
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RightPanel;
